
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ManualUpload from './pages/ManualUpload';
import Dashboard from './pages/Dashboard';
import MyAra from './pages/MyAra';
import Monitoring from './pages/Monitoring';
import RecordDetails from './pages/RecordDetails';
import GroupDetails from './pages/GroupDetails';
import Search from './pages/Search';
import Login from './pages/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApiRootProvider, useApiRoot } from './contexts/ApiRootContext';
import { ToastProvider } from './contexts/ToastContext';
import EnvBanner from './components/EnvBanner';
import AppLoadingScreen from './components/AppLoadingScreen';

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { loading: rootLoading } = useApiRoot();
  if (authLoading) return <AppLoadingScreen message="Checking sign-in..." />;
  if (!user) return <Login />;
  if (rootLoading) return <AppLoadingScreen message="Loading application..." />;
  return <>{children}</>;
}

const LANDING_ROUTES: { rel: string; path: string }[] = [
  { rel: 'search', path: '/search' },
  { rel: 'upload', path: '/upload' },
  { rel: 'dashboard_metrics', path: '/dashboard' },
  { rel: 'monitoring', path: '/monitoring' },
  { rel: 'my', path: '/my-ara' },
];

function HomeRedirect() {
  const { hasLink } = useApiRoot();
  const { signOut } = useAuth();
  const landing = LANDING_ROUTES.find(({ rel }) => hasLink(rel));

  useEffect(() => {
    if (!landing) void signOut();
  }, [landing, signOut]);

  if (!landing) return null;
  return <Navigate to={landing.path} replace />;
}

function RequireLink({ rel, children }: { rel: string; children: React.ReactNode }) {
  const { hasLink } = useApiRoot();
  if (!hasLink(rel)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <ApiRootProvider>
        <div className="min-h-dvh flex flex-col">
          <EnvBanner />

          <Router>
            <AuthGate>
              <ToastProvider>
                <Routes>
                  <Route
                    path="/dashboard"
                    element={
                      <RequireLink rel="dashboard_metrics">
                        <Dashboard />
                      </RequireLink>
                    }
                  />
                  <Route
                    path="/my-ara"
                    element={
                      <RequireLink rel="my">
                        <MyAra />
                      </RequireLink>
                    }
                  />
                  <Route
                    path="/upload"
                    element={
                      <RequireLink rel="upload">
                        <ManualUpload key="upload" />
                      </RequireLink>
                    }
                  />
                  <Route
                    path="/processing/:id"
                    element={
                      <RequireLink rel="upload">
                        <ManualUpload key="processing" />
                      </RequireLink>
                    }
                  />
                  <Route
                    path="/search"
                    element={
                      <RequireLink rel="search">
                        <Search />
                      </RequireLink>
                    }
                  />
                  <Route
                    path="/monitoring"
                    element={
                      <RequireLink rel="monitoring">
                        <Monitoring />
                      </RequireLink>
                    }
                  />
                  <Route path="/records/:id" element={<RecordDetails />} />
                  <Route path="/records/:id/versions/:versionId" element={<RecordDetails />} />
                  <Route path="/groups/:id" element={<GroupDetails />} />
                  <Route path="/" element={<HomeRedirect />} />
                </Routes>
              </ToastProvider>
            </AuthGate>
          </Router>
        </div>
      </ApiRootProvider>
    </AuthProvider>
  );
}

export default App;
